import { useState, forwardRef, useImperativeHandle } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { FileDropZone } from "../FileDropZone";
import { useMentions } from "./useMentions";
import { MentionDropdown } from "./MentionDropdown";
import { At, LockKey, UsersThree, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "../../lib/api";

function VisibilityToggle({ shared, onChange, testPrefix }) {
  const base = "px-2.5 py-1 rounded-full flex items-center gap-1 transition-colors";
  return (
    <div role="radiogroup" aria-label="Note visibility" className="inline-flex rounded-full border border-border p-0.5 bg-muted/40 text-[11px] font-medium" data-testid={`${testPrefix}-visibility`}>
      <button type="button" role="radio" aria-checked={!shared} onClick={() => onChange(false)} data-testid={`${testPrefix}-visibility-internal`}
        className={`${base} ${!shared ? "bg-amber-100 text-amber-900 shadow-sm dark:bg-amber-500/20 dark:text-amber-200" : "text-muted-foreground hover:text-foreground"}`}>
        <LockKey size={11} weight="bold" /> Internal
      </button>
      <button type="button" role="radio" aria-checked={shared} onClick={() => onChange(true)} data-testid={`${testPrefix}-visibility-shared`}
        className={`${base} ${shared ? "bg-emerald-100 text-emerald-900 shadow-sm dark:bg-emerald-500/20 dark:text-emerald-200" : "text-muted-foreground hover:text-foreground"}`}>
        <UsersThree size={11} weight="bold" /> Shared with client
      </button>
    </div>
  );
}

/* Chat composer: text (+ optional @mentions), attachments and — for admins — an Internal / Shared with client switch (defaults to Internal). */
export const NoteComposer = forwardRef(function NoteComposer({ onSend, folder = "notes", allowShare = false, mentions = true, placeholder, sendLabel, testPrefix = "note" }, ref) {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [shared, setShared] = useState(false);
  const [sending, setSending] = useState(false);
  const m = useMentions(text, setText, mentions);

  useImperativeHandle(ref, () => ({
    replyShared() {
      if (allowShare) setShared(true);
      m.textareaRef.current?.focus();
      m.textareaRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    },
  }), [allowShare, m.textareaRef]);

  const send = async () => {
    if (!text.trim() && attachments.length === 0) return;
    setSending(true);
    try {
      await onSend({ text: text.trim(), attachments, shared });
      setText("");
      setAttachments([]);
      setShared(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to send");
    } finally { setSending(false); }
  };

  const onKeyDown = (e) => {
    if (m.open) return m.handleKeyDown(e);
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
  };

  const label = sendLabel || (allowShare ? (shared ? "Send to client" : "Add internal note") : "Send");
  const tone = allowShare ? (shared ? "border-emerald-300/70 dark:border-emerald-800/60" : "border-amber-300/70 dark:border-amber-800/60") : "border-border";

  return (
    <div className={`rounded-lg border ${tone} bg-card p-3 relative transition-colors`} data-testid={`${testPrefix}-composer`}>
      <Textarea
        ref={m.textareaRef}
        value={text}
        onChange={m.onChangeText}
        onKeyDown={onKeyDown}
        rows={2}
        maxLength={5000}
        placeholder={placeholder || (allowShare ? (shared ? "Write a note the client will see…" : "Write an internal note… Type @ to mention a teammate") : "Write a message…")}
        className="mb-2 resize-none text-sm bg-background"
        data-testid={`${testPrefix}-input`}
      />
      <MentionDropdown open={m.open} filtered={m.filtered} idx={m.idx} setIdx={m.setIdx} onPick={m.insertMention} testPrefix={testPrefix} />
      <FileDropZone files={attachments} onFilesChange={setAttachments} folder={folder} accept="all" maxFiles={5} compact className="mb-2" />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {allowShare ? <VisibilityToggle shared={shared} onChange={setShared} testPrefix={testPrefix} />
          : <span className="text-[10px] text-muted-foreground opacity-70 flex items-center gap-1">{mentions && <><At size={10} /> Type @ to mention · </>}Ctrl+Enter to send</span>}
        <Button size="sm" onClick={send} disabled={sending || (!text.trim() && attachments.length === 0)} data-testid={`${testPrefix}-send`}
          className={allowShare && shared ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}>
          <PaperPlaneTilt size={13} className="mr-1" /> {sending ? "Sending…" : label}
        </Button>
      </div>
    </div>
  );
});
